import { Box, Button, ButtonBase } from '@mui/material';
import { PhotoLibraryOutlined } from '@mui/icons-material';
import PhotoPlaceholder from './PhotoPlaceholder';

/** One large photo and four small ones is all a screen has room for; the rest live in the lightbox. */
const MAX_TILES = 5;

/**
 * A grid that always comes out full — an empty cell beside the cover shot reads
 * as a broken image, not as a design. The cover spans both rows only when the
 * remaining photos exactly fill the other columns (3 tiles in two columns, 5 in
 * three); otherwise every tile is the same size and the grid still closes.
 */
const gridFor = (n) => {
  if (n <= 1) return { cols: 1, rows: 1, spanMain: false };
  if (n === 2) return { cols: 2, rows: 1, spanMain: false };
  const cols = n < 5 ? 2 : 3;
  return { cols, rows: 2, spanMain: n - 1 === (cols - 1) * 2 };
};

/**
 * The band of photos at the top of a listing. The first photo carries the page,
 * so it gets the whole left half; the next four sit beside it. Every tile opens
 * the lightbox at its own index, which is what a buyer expects from a portal.
 *
 * A listing with no photos gets the placeholder rather than a collapsed band —
 * the layout below it should not shift depending on what the realtor uploaded.
 */
export default function PhotoMosaic({ photos = [], title = '', onOpen }) {
  const shown = photos.slice(0, MAX_TILES);
  const total = photos.length;

  if (total === 0) return <PhotoPlaceholder height={320} iconSize={64} sx={{ borderRadius: 2 }} />;

  const tile = (p, i, sx) => (
    <ButtonBase
      key={p.url || i}
      onClick={() => onOpen && onOpen(i)}
      aria-label={`Enlarge photo ${i + 1} of ${total} of ${title}`}
      sx={{ display: 'block', overflow: 'hidden', borderRadius: 1.5, ...sx }}
    >
      <Box
        component="img"
        src={p.url}
        alt={`${title} — photo ${i + 1}`}
        loading={i === 0 ? 'eager' : 'lazy'}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
      />
    </ButtonBase>
  );

  const { cols, rows, spanMain } = gridFor(shown.length);

  return (
    <Box sx={{ position: 'relative', mb: 3 }}>
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: cols === 1 ? '1fr' : { xs: '1fr', sm: cols === 2 ? '2fr 1fr' : '2fr 1fr 1fr' },
          gridTemplateRows: rows === 1 ? 'auto' : { xs: 'auto', sm: '1fr 1fr' },
          height: { xs: 'auto', sm: rows === 1 ? 300 : 380 },
        }}
      >
        {tile(shown[0], 0, {
          gridColumn: cols === 1 ? 'auto' : { sm: '1' },
          gridRow: spanMain ? { sm: '1 / span 2' } : 'auto',
          height: { xs: 240, sm: '100%' },
        })}
        {shown.slice(1).map((p, i) => tile(p, i + 1, { display: { xs: 'none', sm: 'block' }, height: '100%' }))}
      </Box>

      {/* The small tiles are hidden on a phone, so on anything but a single-photo
          listing this button is the only way into the rest of the set. It is not
          an overflow affordance — it is the phone buyer's whole gallery. */}
      {total > 1 && (
        <Button
          size="small"
          variant="contained"
          startIcon={<PhotoLibraryOutlined />}
          onClick={() => onOpen && onOpen(0)}
          sx={{
            position: 'absolute', bottom: 12, right: 12, textTransform: 'none', fontWeight: 700,
            borderRadius: 999, bgcolor: '#fff', color: 'text.primary', '&:hover': { bgcolor: '#fff' },
            boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
          }}
        >
          {`See all ${total} photos`}
        </Button>
      )}
    </Box>
  );
}
